import React, { useEffect, useState } from 'react';
import API from '../services/api';
import Button from '../ui/Button';
import LinkStatusList, { fmtName } from '../components/LinkStatusList';

const PAGE_SIZE = 20;

function SendCardLinkPanel({ clients, onSent }) {
  const [clientId, setClientId] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState(null); // { ok, msg }

  useEffect(() => {
    if (!clientId) { setEmail(''); return; }
    let cancelled = false;
    API.get(`/admin/clients/${clientId}/billing-email`)
      .then((r) => { if (!cancelled && r.data?.success) setEmail(r.data.email || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  const send = async () => {
    if (!clientId) { setNote({ ok: false, msg: 'Select a client first.' }); return; }
    if (!email.trim()) { setNote({ ok: false, msg: 'An email address is required.' }); return; }
    setLoading(true); setNote(null);
    try {
      const res = await API.post('/forms/card-update/generate', { clientId, email: email.trim(), message });
      if (res.data?.success) {
        setNote({ ok: true, msg: `Card-update link sent to ${email.trim()}` });
        setMessage('');
        onSent?.();
      } else {
        setNote({ ok: false, msg: res.data?.message || 'Failed to send link' });
      }
    } catch (e) {
      setNote({ ok: false, msg: e.response?.data?.message || 'Error sending link' });
    } finally { setLoading(false); }
  };

  const field = { width: '100%', padding: 8, marginBottom: 10, boxSizing: 'border-box' };

  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, padding: 16, marginBottom: 24, maxWidth: 480 }}>
      <h3 style={{ marginTop: 0 }}>Send a Card-Update Link</h3>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
        Emails a client a secure link to add or update their card. It appears below once they submit it.
      </p>
      <label style={{ fontWeight: 600, fontSize: 13 }}>Client</label>
      <select style={field} value={clientId} onChange={(e) => setClientId(e.target.value)}>
        <option value="">-- Select a client --</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>{fmtName(c.client_name)}</option>
        ))}
      </select>
      <label style={{ fontWeight: 600, fontSize: 13 }}>Client Email</label>
      <input style={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" />
      <label style={{ fontWeight: 600, fontSize: 13 }}>Message (optional)</label>
      <textarea style={{ ...field, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a short note…" />
      {note && <div style={{ fontSize: 13, marginBottom: 8, color: note.ok ? 'var(--success-color)' : 'var(--error-color)' }}>{note.msg}</div>}
      <Button variant="primary" onClick={send} disabled={loading}>{loading ? 'Sending…' : '✉ Send Card-Update Link'}</Button>
    </div>
  );
}

export default function AdminPaymentLinksPage() {
  const [links, setLinks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const loadLinks = () => {
    API.get('/admin/payment-links')
      .then((res) => setLinks(res.data || []))
      .catch(() => setLinks([]));
  };
  useEffect(() => { loadLinks(); }, []);
  useEffect(() => {
    API.get('/admin/all-clients-all').then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const visible = links.filter((l) => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (search && !fmtName(l.clientName).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const shown = visible.slice(0, visibleCount);

  return (
    <div style={{ padding: '1rem' }}>
      <h2>Payment Links</h2>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
        Status of card-collection links sent to clients — intake/welcome links and card-update links.
      </p>

      <SendCardLinkPanel clients={clients} onSent={loadLinks} />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '1rem 0' }}>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setVisibleCount(PAGE_SIZE); }}>
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="expired">Expired</option>
        </select>
        <input type="text" placeholder="Search client…" value={search} onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }} />
      </div>

      {visible.length === 0 ? (
        <p>No links found.</p>
      ) : (
        <>
          <LinkStatusList rows={shown} showSentTo />
          <div style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: 13 }}>
            Showing {shown.length} of {visible.length}
            {visible.length > visibleCount && (
              <Button variant="subtle" style={{ marginLeft: 12 }} onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                Show more
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
